// client/src/coms/views/canvas_parts/canvas_room_view_parts/brushImport/brushPreview.js
//
// Renders sample strokes with a brush's translated settings: the same dab
// planner and the same alpha-darken compositing model as the GPU stroke
// session, evaluated on the CPU so many small previews can be produced
// without extra WebGL contexts. Previews show a pressure S-curve (light ->
// heavy -> light), a quick straight stroke, and a long pressure ramp.
import { applyEndTaper, createStrokeState, importedStrokeSessionOptions, planDabs } from './dabPlanner.js';

function createCanvas(width, height) {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  return canvas;
}

function hexToRgb(hex) {
  const text = String(hex || '#000000').replace('#', '');
  const value = parseInt(text.padEnd(6, '0').slice(0, 6), 16) || 0;
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function tipImageData(canvas) {
  if (!canvas) return null;
  if (!canvas.__previewImageData) {
    canvas.__previewImageData = canvas.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, canvas.width, canvas.height);
  }
  return canvas.__previewImageData;
}

function grainSampler(brush) {
  const texture = brush.brushTexture;
  const canvas = texture ? brush.originalGrainCanvas : null;
  if (!canvas) return null;
  const data = tipImageData(canvas);
  const size = canvas.width;
  return (x, y, originX, originY, brushSize) => {
    const relative = texture.mode === 'moving' ? Math.max(8, brushSize) / 64 : 1;
    const scale = Math.max(4, (canvas.grainSourceSize || size) * Math.max(0.01, texture.scale || 1) * relative);
    const px = x - originX;
    const py = y - originY;
    const c = Math.cos(texture.rotation || 0);
    const s = Math.sin(texture.rotation || 0);
    let u = ((c * px - s * py) / scale) % 1;
    let v = ((s * px + c * py) / scale) % 1;
    if (u < 0) u += 1;
    if (v < 0) v += 1;
    // Textures are uploaded flipped; sample the same row the GPU would.
    const gx = Math.min(size - 1, Math.floor(u * size));
    const gy = Math.min(size - 1, Math.floor((1 - v) * size));
    let g = data.data[(gy * size + gx) * 4] / 255;
    g = Math.max(0, Math.min(1, (g - 0.5) * (1 + (texture.contrast || 0)) + 0.5 + (texture.brightness || 0)));
    if (texture.invert) g = 1 - g;
    const depth = Math.max(0, Math.min(1, texture.depth == null ? 1 : texture.depth));
    const factor = texture.blend === 'subtract' ? Math.max(0, Math.min(1, 1 - depth * (1 - g) * 2)) : 1 + (g - 1) * depth;
    return Math.max(factor, texture.depthMinimum || 0);
  };
}

function selectTip(brush, dab) {
  const tips = brush.originalTipCanvases && brush.originalTipCanvases.length ? brush.originalTipCanvases : [brush.originalTipCanvas];
  if (tips.length <= 1) return tips[0];
  const mode = brush.tipSelection || 'random';
  let index;
  if (mode === 'sequential') index = Math.abs(Math.floor(dab.tipIndex || 0));
  else if (mode === 'pressure') index = Math.round(Math.max(0, Math.min(1, dab.pressure || 1)) * (tips.length - 1));
  else index = Math.abs(Math.floor(Math.sin((dab.seed || 1) * 12.9898) * 43758.5453));
  return tips[index % tips.length];
}

// Composites dabs into coverage/colour buffers with the GPU's rules.
function compositeDabs(buffers, dabs, brush, width, height, scale) {
  const grain = grainSampler(brush);
  const scratch = buffers.scratch;
  const sctx = scratch.getContext('2d', { willReadFrequently: true });
  for (const dab of dabs) {
    const tip = selectTip(brush, dab);
    if (!tip) continue;
    const size = dab.size * scale;
    const longest = Math.max(tip.width, tip.height);
    const halfW = Math.abs(size * (tip.width / longest) * 0.5 * (dab.scaleX || 1));
    const halfH = Math.abs(size * (tip.height / longest) * 0.5 * (dab.scaleY || 1));
    const radius = Math.ceil(Math.hypot(halfW, halfH)) + 1;
    const cx = dab.x * scale;
    const cy = dab.y * scale;
    const x0 = Math.max(0, Math.floor(cx - radius));
    const y0 = Math.max(0, Math.floor(cy - radius));
    const x1 = Math.min(width, Math.ceil(cx + radius));
    const y1 = Math.min(height, Math.ceil(cy + radius));
    const w = x1 - x0;
    const h = y1 - y0;
    if (w <= 0 || h <= 0) continue;
    if (scratch.width < w || scratch.height < h) {
      scratch.width = Math.max(scratch.width, w);
      scratch.height = Math.max(scratch.height, h);
    }
    sctx.setTransform(1, 0, 0, 1, 0, 0);
    sctx.clearRect(0, 0, w, h);
    sctx.setTransform(1, 0, 0, 1, cx - x0, cy - y0);
    sctx.rotate(dab.rotation || 0);
    sctx.scale(dab.scaleX || 1, dab.scaleY || 1);
    sctx.imageSmoothingEnabled = true;
    sctx.drawImage(tip, -size * (tip.width / longest) * 0.5, -size * (tip.height / longest) * 0.5, size * (tip.width / longest), size * (tip.height / longest));
    const pixels = sctx.getImageData(0, 0, w, h).data;
    const colour = dab.colour || buffers.baseColour;
    const useTipColour = brush.tipColour === 'image';
    for (let y = 0; y < h; y += 1) {
      for (let x = 0; x < w; x += 1) {
        const i = (y * w + x) * 4;
        const a = pixels[i + 3] / 255;
        const k = Math.min(1, a * dab.flow);
        if (k <= 0.0005) continue;
        const px = x0 + x;
        const py = y0 + y;
        let target = dab.opacity == null ? 1 : dab.opacity;
        if (grain) target *= grain(px / scale, py / scale, dab.grainX || 0, dab.grainY || 0, buffers.brushSize);
        const p = py * width + px;
        buffers.coverage[p] += (target - buffers.coverage[p]) * k;
        const r = useTipColour ? pixels[i] / 255 : colour[0];
        const g = useTipColour ? pixels[i + 1] / 255 : colour[1];
        const b = useTipColour ? pixels[i + 2] / 255 : colour[2];
        buffers.r[p] += (r * target - buffers.r[p]) * k;
        buffers.g[p] += (g * target - buffers.g[p]) * k;
        buffers.b[p] += (b * target - buffers.b[p]) * k;
      }
    }
  }
}

function strokePaths(width, height) {
  const paths = [];
  const s = [];
  for (let i = 0; i <= 60; i += 1) {
    const t = i / 60;
    s.push({ x: width * (0.06 + t * 0.56), y: height * (0.5 + Math.sin(t * Math.PI * 2) * 0.24), pressure: Math.sin(t * Math.PI) * 0.92 + 0.05, tiltX: 0, tiltY: 0 });
  }
  paths.push({ points: s, msPerStep: 14 });
  const quick = [];
  for (let i = 0; i <= 12; i += 1) {
    const t = i / 12;
    quick.push({ x: width * (0.68 + t * 0.27), y: height * (0.22 + t * 0.1), pressure: 0.75 });
  }
  paths.push({ points: quick, msPerStep: 3 });
  const ramp = [];
  for (let i = 0; i <= 24; i += 1) {
    const t = i / 24;
    ramp.push({ x: width * (0.68 + t * 0.27), y: height * (0.72 - t * 0.04), pressure: 0.08 + t * 0.9 });
  }
  paths.push({ points: ramp, msPerStep: 18 });
  return paths;
}

export function renderBrushStrokePreview(brush, { width = 320, height = 112, pixelRatio = 1, colour = '#1b1d22', background = null, size = null, strokes = null } = {}) {
  const outW = Math.round(width * pixelRatio);
  const outH = Math.round(height * pixelRatio);
  const canvas = createCanvas(outW, outH);
  if (!brush || !brush.originalTipCanvas || !brush.brushDynamics) return canvas;
  const count = outW * outH;
  const buffers = {
    coverage: new Float32Array(count),
    r: new Float32Array(count),
    g: new Float32Array(count),
    b: new Float32Array(count),
    scratch: createCanvas(64, 64),
    baseColour: hexToRgb(colour).map((v) => v / 255),
    brushSize: 0,
  };
  const brushSize = size || Math.max(6, Math.min(height * 0.32, 40));
  buffers.brushSize = brushSize;
  const options = { size: brushSize, color: colour, opacity: 1, strokeSeed: 4242 };
  // Erasers are previewed as their shape; erasing an empty preview shows nothing.
  const sessionOptions = { ...importedStrokeSessionOptions(brush, options), isEraser: false };
  const paths = strokes || strokePaths(width, height);
  paths.forEach((path, pathIndex) => {
    const state = createStrokeState({ logDabs: true });
    let clock = 1000;
    let dabIndex = 0;
    const strokeOptions = { ...options, strokeSeed: 4242 + pathIndex * 97 };
    const all = [];
    for (let i = 0; i < path.points.length; i += 1) {
      const start = path.points[Math.max(0, i - 1)];
      const end = path.points[i];
      clock += path.msPerStep || 12;
      const result = planDabs(start, i === 0 ? null : end, brush, strokeOptions, state, dabIndex, i === 0, clock);
      dabIndex = result.dabIndex;
      all.push(...result.dabs);
    }
    const tapered = applyEndTaper(state.dabLog, brush, strokeOptions) || all;
    const before = buffers.coverage.slice();
    buffers.coverage.fill(0);
    const rgbBefore = [buffers.r.slice(), buffers.g.slice(), buffers.b.slice()];
    buffers.r.fill(0);
    buffers.g.fill(0);
    buffers.b.fill(0);
    compositeDabs(buffers, tapered, brush, outW, outH, pixelRatio);
    // Fold this stroke over the earlier ones at the stroke opacity.
    const op = sessionOptions.strokeOpacity;
    for (let p = 0; p < count; p += 1) {
      const a = Math.min(1, buffers.coverage[p] * op);
      const strokeCov = buffers.coverage[p] || 1;
      const sr = buffers.coverage[p] > 0.0001 ? buffers.r[p] / strokeCov : 0;
      const sg = buffers.coverage[p] > 0.0001 ? buffers.g[p] / strokeCov : 0;
      const sb = buffers.coverage[p] > 0.0001 ? buffers.b[p] / strokeCov : 0;
      const prevA = before[p];
      const outA = a + prevA * (1 - a);
      buffers.r[p] = sr * a + rgbBefore[0][p] * (1 - a);
      buffers.g[p] = sg * a + rgbBefore[1][p] * (1 - a);
      buffers.b[p] = sb * a + rgbBefore[2][p] * (1 - a);
      buffers.coverage[p] = sessionOptions.isEraser ? prevA * (1 - a) : outA;
    }
  });

  const ctx = canvas.getContext('2d');
  if (background) {
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, outW, outH);
  }
  const out = ctx.createImageData(outW, outH);
  for (let p = 0; p < count; p += 1) {
    const a = buffers.coverage[p];
    if (a <= 0.002) continue;
    out.data[p * 4] = Math.round(Math.min(1, buffers.r[p] / a) * 255);
    out.data[p * 4 + 1] = Math.round(Math.min(1, buffers.g[p] / a) * 255);
    out.data[p * 4 + 2] = Math.round(Math.min(1, buffers.b[p] / a) * 255);
    out.data[p * 4 + 3] = Math.round(Math.min(1, a) * 255);
  }
  if (background) {
    const layer = createCanvas(outW, outH);
    layer.getContext('2d').putImageData(out, 0, 0);
    ctx.drawImage(layer, 0, 0);
  } else {
    ctx.putImageData(out, 0, 0);
  }
  return canvas;
}
